// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Image, Modal, Pressable, StyleSheet, TextInput, Platform, ScrollView } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Text } from '@/components/Themed';
import { useUser } from './user-context';
import { useI18n } from '@/i18n';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { useRegisterUser } from '@/hooks/useRegisterUser';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { loginWithPassword } from '@/auth/oidc';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';

const REGISTER_HERO_IMAGE_OPTIONS = {
  width: 1200,
  height: 474,
  bucketStep: 1,
} as const;

export default function RegisterScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { user, setUser } = useUser();
  const router = useRouter();
  const { debug: debugParam } = useLocalSearchParams<{ debug?: string }>();
  const debugEnabled = String(debugParam ?? '').toLowerCase() === 'true';
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const loginHeroImage = useMemo(
    () => getAssetImageSource('images/login-hero.png', '', '/register', REGISTER_HERO_IMAGE_OPTIONS),
    []
  );

  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const { loading: redirecting, handleRedirect } = useAuthRedirect({
    user,
    setUser,
    onError: setRedirectError,
    debug: debugEnabled,
  });

  const handleRegistrationSuccess = useCallback(async () => {
    setRedirectError(null);
    const loginEmail = email.trim();
    try {
      const auth = await loginWithPassword(loginEmail, password);
      useRoundState.getState().clear();
      setUser({
        id: auth.id,
        forename: auth.name,
        token: auth.accessToken,
        idToken: auth.idToken ?? null,
        refreshToken: auth.refreshToken ?? null,
      });
      router.replace('/welcome');
      return;
    } catch {
      // Fall back to browser-based OIDC flow if direct grant is disabled or fails.
    }

    await handleRedirect();
  }, [email, handleRedirect, password, router, setUser]);

  const { status, error, loading, socketState, sendCreateRequest } = useRegisterUser({
    apiBaseUrl,
    isFocused,
    displayName,
    fullName,
    email,
    password,
    onReset: () => {
      setDisplayName('');
      setFullName('');
      setEmail('');
      setPassword('');
      setTermsAccepted(false);
      setTermsError(null);
    },
    onSuccess: () => {
      void handleRegistrationSuccess();
    },
  });

  const effectiveError = termsError ?? redirectError ?? error;
  const isBusy = loading || redirecting;

  const handleCreatePress = useCallback(() => {
    if (!termsAccepted) {
      setTermsError(t('register.validationAcceptTerms'));
      return;
    }
    setTermsError(null);
    setRedirectError(null);
    sendCreateRequest();
  }, [sendCreateRequest, t, termsAccepted]);

  const handleWebSubmit = useCallback((event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();
    handleCreatePress();
  }, [handleCreatePress]);

  if (!isNavigationReady) return null;
  if (user) return <Redirect href="/welcome" />;

  const card = (
    <AuthStatusCard
      style={styles.card}
      title={t('register.cardTitle')}
      status={status}
      error={effectiveError}
      buttonLabel={isBusy ? t('register.creating') : t('register.createCta')}
      onPress={handleCreatePress}
      disabled={isBusy}
    >
      <Text style={styles.label}>{t('register.displayNameLabel')}</Text>
      <TextInput
        value={displayName}
        onChangeText={setDisplayName}
        placeholder={t('register.displayNamePlaceholder')}
        style={styles.input}
        autoCapitalize="words"
        autoComplete="nickname"
        placeholderTextColor="#8FA08B"
      />
      <Text style={styles.label}>{t('register.fullNameLabel')}</Text>
      <TextInput
        value={fullName}
        onChangeText={setFullName}
        placeholder={t('register.fullNamePlaceholder')}
        style={styles.input}
        autoCapitalize="words"
        autoComplete="name"
        placeholderTextColor="#8FA08B"
      />
      <Text style={styles.label}>{t('register.emailLabel')}</Text>
      <TextInput
        value={email}
        onChangeText={setEmail}
        placeholder={t('register.emailPlaceholder')}
        style={styles.input}
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="email"
        placeholderTextColor="#8FA08B"
      />
      <Text style={styles.label}>{t('register.passwordLabel')}</Text>
      <TextInput
        value={password}
        onChangeText={setPassword}
        placeholder={t('register.passwordPlaceholder')}
        style={styles.input}
        secureTextEntry
        autoComplete="new-password"
        placeholderTextColor="#8FA08B"
      />
      <Pressable
        style={styles.termsRow}
        onPress={() => {
          setTermsAccepted((current) => !current);
          setTermsError(null);
        }}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: termsAccepted }}
      >
        <Pressable
          style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}
          onPress={() => {
            setTermsAccepted((current) => !current);
            setTermsError(null);
          }}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: termsAccepted }}
        >
          {termsAccepted ? <Text style={styles.checkboxTick}>✓</Text> : null}
        </Pressable>
        <Text style={styles.termsLabel}>{t('register.acceptTermsLabel')}</Text>
      </Pressable>
      <Pressable style={styles.linkSmall} onPress={() => setTermsOpen(true)}>
        <Text style={styles.linkSmallText}>{t('register.openTermsCta')}</Text>
      </Pressable>
      {debugEnabled ? (
        <Text style={styles.connection}>
          {t('register.connectionLabel', { status: socketState === 'connected' ? t('register.connectionUp') : t('register.connectionDown') })}
        </Text>
      ) : null}
      <Pressable style={styles.link} onPress={() => router.replace('/')}>
        <Text style={styles.linkText}>{t('register.backToLogin')}</Text>
      </Pressable>
    </AuthStatusCard>
  );

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <Image source={loginHeroImage} style={styles.hero} resizeMode="contain" />
      {Platform.OS === 'web'
        ? <form onSubmit={handleWebSubmit} style={styles.webForm as never}>{card}</form>
        : card}
      <Modal transparent visible={termsOpen} animationType="fade" onRequestClose={() => setTermsOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setTermsOpen(false)}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>{t('register.termsTitle')}</Text>
            <Text style={styles.modalText}>{t('register.termsSummary')}</Text>
            <Text style={styles.modalBullet}>{`\u2022 ${t('register.termsRuleAccount')}`}</Text>
            <Text style={styles.modalBullet}>{`\u2022 ${t('register.termsRulePrivacy')}`}</Text>
            <Text style={styles.modalBullet}>{`\u2022 ${t('register.termsRuleFairUse')}`}</Text>
            <Text style={styles.modalBullet}>{`\u2022 ${t('register.termsRuleNoWarranty')}`}</Text>
            <Text style={styles.modalText}>{t('register.termsUpdated')}</Text>
            <Pressable style={styles.modalButton} onPress={() => setTermsOpen(false)}>
              <Text style={styles.modalButtonText}>{t('register.closeTermsCta')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    alignItems: 'center',
    gap: 12,
  },
  scroll: { flex: 1, backgroundColor: '#011806' },
  hero: {
    width: '100%',
    maxWidth: 720,
    aspectRatio: 1200 / 474,
    marginTop: 20,
  },
  card: {
    width: '100%',
    maxWidth: 900,
  },
  webForm: {
    width: '100%',
    maxWidth: 900,
  },
  label: {
    color: appColors.text.primary,
    fontWeight: '700',
    marginBottom: 6,
    marginTop: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    backgroundColor: '#0C2510',
  },
  termsRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  checkboxChecked: {
    backgroundColor: '#A1D493',
  },
  checkboxTick: {
    color: '#06230C',
    fontWeight: '800',
    fontSize: 12,
  },
  termsLabel: {
    color: appColors.text.primary,
    flexShrink: 1,
  },
  linkSmall: {
    marginTop: -4,
    alignSelf: 'flex-start',
  },
  linkSmallText: {
    color: '#A1D493',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  connection: {
    color: '#8FA08B',
    fontSize: 12,
    marginTop: 12,
  },
  link: {
    marginTop: 12,
    alignItems: 'center',
  },
  linkText: {
    color: '#A1D493',
    fontWeight: '700',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    padding: 16,
    gap: 10,
  },
  modalTitle: {
    color: appColors.text.primary,
    fontSize: 20,
    fontWeight: '700',
  },
  modalText: {
    color: '#DDE5D8',
    lineHeight: 20,
  },
  modalBullet: {
    color: '#DDE5D8',
    lineHeight: 20,
    paddingLeft: 4,
  },
  modalButton: {
    marginTop: 6,
    backgroundColor: '#A1D493',
    borderRadius: 10,
    alignItems: 'center',
    paddingVertical: 10,
  },
  modalButtonText: {
    color: '#06230C',
    fontWeight: '800',
  },
});
